import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Printer, FileText } from "lucide-react";
import { triggerBlobDownload } from "../../lib/download";
import { printAllHhbLetters, printHhbEnvelopesOnly, markHhbLetterSent, listHhbLetters } from "../../lib/api";

const PRINT_CHOICE_KEY = "hhb_print_choice";

// Reusable HHB letter print panel: print-option dropdown (incl. window-envelope /
// address-page fold option) + Print button + per-letter "Mark sent" controls.
// Used by the Letter Wizard (Step 4, right after finalizing) and by the
// investigation dialog's "Print letters" reprint dialog. Reprinting never
// creates new letter versions (it only hits the print/envelope endpoints).
export default function HhbPrintPanel({ invId, stage }) {
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printChoice, setPrintChoice] = useState(() => {
    try { return localStorage.getItem(PRINT_CHOICE_KEY) || "letters"; } catch { return "letters"; }
  });
  const [duplex, setDuplex] = useState(() => {
    try { return localStorage.getItem("hhb_duplex") === "1"; } catch { return false; }
  });

  const reload = useCallback(async () => {
    try {
      const list = await listHhbLetters(invId);
      setLetters((list.data || []).filter((l) => l.stage === stage && !l.superseded));
    } catch { /* ignore */ }
    setLoading(false);
  }, [invId, stage]);

  useEffect(() => { reload(); }, [reload]);

  const onChoice = (v) => {
    setPrintChoice(v);
    try { localStorage.setItem(PRINT_CHOICE_KEY, v); } catch { /* ignore */ }
  };

  const runPrint = async () => {
    try {
      if (printChoice === "letters") {
        const res = await printAllHhbLetters(invId, stage, false, false, duplex);
        triggerBlobDownload(res.data, `HHB_${stage}_letters.pdf`);
      } else if (printChoice === "address_page") {
        const res = await printAllHhbLetters(invId, stage, false, true);
        triggerBlobDownload(res.data, `HHB_${stage}_letters_window_fold.pdf`);
      } else if (printChoice === "combined") {
        const res = await printAllHhbLetters(invId, stage, true);
        triggerBlobDownload(res.data, `HHB_${stage}_letters_with_envelopes.pdf`);
      } else {
        // two separate PDFs — letters first, then envelopes in the same order
        const lres = await printAllHhbLetters(invId, stage, false, false, duplex);
        triggerBlobDownload(lres.data, `HHB_${stage}_letters.pdf`);
        const eres = await printHhbEnvelopesOnly(invId, stage);
        triggerBlobDownload(eres.data, `HHB_${stage}_envelopes.pdf`);
      }
    } catch { toast.error("Print failed"); }
  };

  const toggleDuplex = (v) => {
    setDuplex(v);
    try { localStorage.setItem("hhb_duplex", v ? "1" : "0"); } catch { /* ignore */ }
  };

  const markSent = async (lid, method) => {
    try {
      await markHhbLetterSent(invId, lid, { method });
      await reload();
      toast.success("Marked sent");
    } catch { toast.error("Failed"); }
  };

  if (loading) return <div className="h-24 bg-gray-100 rounded animate-pulse" data-testid="hhb-print-panel-loading" />;
  if (!letters.length) return <p className="text-sm text-muted-foreground" data-testid="hhb-print-panel-empty">No finalized letters for this stage.</p>;

  return (
    <div className="space-y-3" data-testid="hhb-print-panel">
      <div className="flex items-end gap-2 flex-wrap">
        <div className="space-y-1">
          <Label className="text-xs">Print option</Label>
          <Select value={printChoice} onValueChange={onChoice}>
            <SelectTrigger className="w-[340px]" data-testid="wiz-print-choice"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="letters" data-testid="wiz-print-choice-letters">Letters only (for window envelopes)</SelectItem>
              <SelectItem value="address_page" data-testid="wiz-print-choice-address-page">Letters with address page (fold for #10 window envelope)</SelectItem>
              <SelectItem value="two_pdfs" data-testid="wiz-print-choice-two">Letters + Envelopes as two separate PDFs</SelectItem>
              <SelectItem value="combined" data-testid="wiz-print-choice-combined">Combined PDF (letter, envelope, letter, envelope…)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" variant="outline" onClick={runPrint} data-testid="wiz-print-run"><Printer className="w-4 h-4 mr-1" />Print</Button>
      </div>
      {(printChoice === "letters" || printChoice === "two_pdfs") && (
        <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="wiz-duplex-label">
          <input type="checkbox" checked={duplex} onChange={(e) => toggleDuplex(e.target.checked)} data-testid="wiz-duplex" />
          Duplex printing — add a blank page so each letter starts on a fresh sheet (no letter prints on the back of another)
        </label>
      )}
      {printChoice === "address_page" && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="wiz-print-address-page-note">
          Print double-sided (flip on long edge). Each letter is followed by an address page on the back of its last sheet — tri-fold so both addresses show through a #10 double-window envelope. Nudge the alignment in Settings ▸ School Branding (HHB window offsets) and use the "Window alignment test".
        </p>
      )}
      {printChoice === "combined" && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="wiz-print-combined-note">
          Open in Adobe Acrobat Reader and check "Choose paper source by PDF page size" so envelopes feed from the manual tray automatically.
        </p>
      )}
      {letters.map((l) => (
        <div key={l.id} className="border rounded p-2 flex items-center justify-between gap-2 text-sm" data-testid={`wiz-final-${l.id}`}>
          <div className="min-w-0">
            <span className="font-medium">{l.label}</span> → {l.recipient_name} {l.version > 1 && <Badge className="ml-1 bg-gray-200 text-gray-700">v{l.version}</Badge>}
            {l.sent && <span className="text-emerald-700 text-xs ml-2">Sent {l.sent.date} · {l.sent.method}</span>}
          </div>
          {!l.sent ? (
            <Select onValueChange={(m) => markSent(l.id, m)}>
              <SelectTrigger className="h-7 w-32 text-xs" data-testid={`wiz-mark-sent-${l.id}`}><SelectValue placeholder="Mark sent…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Mail">Mail</SelectItem>
                <SelectItem value="Email">Email</SelectItem>
                <SelectItem value="Hand delivered">Hand delivered</SelectItem>
              </SelectContent>
            </Select>
          ) : <FileText className="w-4 h-4 text-emerald-600" />}
        </div>
      ))}
    </div>
  );
}

HhbPrintPanel.displayName = "HhbPrintPanel";
